<template>
  <AuthShell
    :title="$t('register.heroTitle')"
    :subtitle="$t('register.heroSubtitle')"
  >
    <div class="space-y-6">
      <div>
        <p class="text-sm uppercase tracking-[0.3em] text-steel-400">{{ $t('register.eyebrow') }}</p>
        <h2 class="mt-3 font-display text-2xl font-semibold text-white">{{ $t('register.title') }}</h2>
      </div>

      <button
        type="button"
        class="flex w-full items-center justify-center gap-3 rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-sm font-medium text-white transition hover:border-white/30 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="isLoading"
        @click="signUpWithGoogle"
      >
        <svg class="h-5 w-5" viewBox="0 0 48 48" aria-hidden="true">
          <path
            fill="#FFC107"
            d="M43.611 20.083H42V20H24v8h11.303C33.45 32.91 29.145 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.96 3.04l5.657-5.657C34.046 6.053 29.247 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.651-.389-3.917Z"
          />
          <path
            fill="#FF3D00"
            d="M6.306 14.691 12.17 18.987C13.758 15.147 18.143 12 24 12c3.059 0 5.842 1.154 7.96 3.04l5.657-5.657C34.046 6.053 29.247 4 24 4 16.318 4 9.656 8.348 6.306 14.691Z"
          />
          <path
            fill="#4CAF50"
            d="M24 44c5.047 0 9.749-1.932 13.258-5.077l-6.121-5.182C29.065 35.481 26.645 36 24 36c-5.126 0-9.42-3.063-11.29-7.43l-5.889 4.54C9.129 39.556 16.073 44 24 44Z"
          />
          <path
            fill="#1976D2"
            d="M43.611 20.083H42V20H24v8h11.303a12.03 12.03 0 0 1-4.166 5.741l.002-.001 6.121 5.182C36.858 39.294 44 34 44 24c0-1.341-.138-2.651-.389-3.917Z"
          />
        </svg>
        {{ $t('register.google') }}
      </button>

      <div class="flex items-center gap-4 text-xs uppercase tracking-[0.35em] text-steel-400">
        <span class="h-px flex-1 bg-white/10"></span>
        {{ $t('register.or') }}
        <span class="h-px flex-1 bg-white/10"></span>
      </div>

      <form class="space-y-5" @submit.prevent="signUpWithEmail">
        <label class="block text-sm text-steel-200">
          {{ $t('register.fullName') }}
          <input
            type="text"
            placeholder="Ari Valen"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            v-model.trim="fullName"
            autocomplete="name"
          />
        </label>

        <label class="block text-sm text-steel-200">
          {{ $t('register.username') }}
          <input
            type="text"
            placeholder="drvnmember"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            v-model.trim="handle"
            autocomplete="username"
          />
        </label>

        <label class="block text-sm text-steel-200">
          {{ $t('register.email') }}
          <input
            type="email"
            placeholder="driver@drvn.co"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            v-model.trim="email"
            autocomplete="email"
          />
        </label>

        <label class="block text-sm text-steel-200">
          {{ $t('register.password') }}
          <input
            type="password"
            placeholder="Minimum 8 characters"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            v-model="password"
            autocomplete="new-password"
          />
        </label>

        <label class="block text-sm text-steel-200">
          {{ $t('register.confirmPassword') }}
          <input
            type="password"
            placeholder="Repeat your password"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            v-model="confirmPassword"
            autocomplete="new-password"
          />
        </label>

        <label class="flex items-start gap-3 text-xs text-steel-400">
          <input type="checkbox" class="mt-1 h-4 w-4 rounded border-white/20 bg-midnight-800 text-neon-400" />
          {{ $t('register.terms') }}
        </label>

        <button
          type="submit"
          class="w-full rounded-2xl bg-white px-4 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
          :disabled="isLoading"
        >
          {{ isLoading ? $t('register.submitting') : $t('register.submit') }}
        </button>
      </form>

      <p v-if="errorMessage" class="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
        {{ errorMessage }}
      </p>

      <p class="text-center text-xs text-steel-400">
        {{ $t('register.alreadyHave') }}
        <router-link class="text-neon-400 hover:text-neon-500" to="/login">{{ $t('register.signIn') }}</router-link>
      </p>
    </div>
  </AuthShell>
</template>

<script>
import AuthShell from '@/components/auth/AuthShell.vue'
import { supabase } from '@/lib/supabase'

export default {
  name: 'RegisterView',
  components: {
    AuthShell,
  },
  data() {
    return {
      fullName: '',
      handle: '',
      email: '',
      password: '',
      confirmPassword: '',
      isLoading: false,
      errorMessage: '',
    }
  },
  methods: {
    async signUpWithEmail() {
      if (!this.email || !this.password || !this.confirmPassword) {
        this.errorMessage = this.$t('register.missingFields')
        return
      }

      if (!this.handle) {
        this.errorMessage = this.$t('register.chooseHandle')
        return
      }

      const normalizedHandle = this.handle.toLowerCase()

      const { data: handleExists } = await supabase
        .from('profiles')
        .select('id')
        .eq('handle', normalizedHandle)
        .maybeSingle()

      if (handleExists) {
        this.errorMessage = this.$t('register.handleTaken')
        return
      }

      if (this.password !== this.confirmPassword) {
        this.errorMessage = this.$t('register.passwordMismatch')
        return
      }

      this.isLoading = true
      this.errorMessage = ''
      const { data, error } = await supabase.auth.signUp({
        email: this.email,
        password: this.password,
        options: {
          data: {
            full_name: this.fullName,
            handle: normalizedHandle,
          },
        },
      })
      this.isLoading = false

      if (error) {
        this.errorMessage = error.message
      } else {
        const userId = data?.user?.id
        if (userId) {
          await supabase.from('profiles').insert({
            id: userId,
            handle: normalizedHandle,
            full_name: this.fullName,
          })
        }
        this.errorMessage = this.$t('register.accountCreated')
        this.$router.push('/login')
      }
    },
    async signUpWithGoogle() {
      this.isLoading = true
      this.errorMessage = ''
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: 'https://drvnsocial.com/auth/callback',
        },
      })
      this.isLoading = false

      if (error) {
        this.errorMessage = error.message
      }
    },
  },
}
</script>
